import {Component, OnInit, ElementRef, ViewChild, Output, EventEmitter, Input} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ExportToCsv } from 'export-to-csv';

import { CDSNode, Rule } from '../../services/capd.model';
import { RulesService } from '../../services/rules.service';
import { OrgService } from '../../services/org.service';
import {AlertService} from "../../services/alert.service";
import {CDSTreeService} from "../../services/cds.service";

declare var $: any;

@Component({
    templateUrl: './group.component.html'
})

/**
 * Parent component for all group content.
 */
export class GroupContentComponentV2 implements OnInit {
    root: string;
    ruleList: Rule[] = [];
    org: CDSNode;
    data: any = [];
    ruleEdits: Rule[] = [];

    @ViewChild('cboxConfirmRef') cboxConfirmRef: ElementRef;

    // Group delete rules modal variables
    @Output() ruleDeleted = new EventEmitter();
    isDisabled = true;
    infoRule: Rule;

    watermarkText = '<span>Loading...<br /><img src="./app/css/img/linear-loader.gif" alt=""/></span>';

    private sub: any;

    constructor(
        private _router: Router,
        private _route: ActivatedRoute,
        private _el: ElementRef,
        private _rules: RulesService,
        private _org: OrgService,
        private _cds: CDSTreeService,
        private alertService: AlertService) {}

    /**
     * Gets the current group.
     *
     * @async
     * @func ngOnInit
     */
    ngOnInit() {
        let orgRoot;
        let groupExt;
        this.sub = this._route.params.subscribe(params => {
            this.root = params.id;
            this.watermarkText = '<span>Loading...<br /><img src="./app/css/img/linear-loader.gif" alt=""/></span>';

            orgRoot = this.root.split('+')[0];
            groupExt = this.root.split('+')[1];

            this.doUpdate(orgRoot, groupExt);

            this._org.getGroup(orgRoot, groupExt).then((org: CDSNode) => {
                if (org !== undefined) {
                    this.org = org;
                } else {
                    this._org.getGroupsById(orgRoot).then((groups: CDSNode[]) => {
                        let groupList = groups.filter(node => node.extensionId == groupExt);
                        this.org = groupList[0];
                    });
                }
            });
        });

        this._rules.getGroupContentV2(this.root).then(res => {
            this.ruleList = res
            this.createCsv();

            if(this.ruleList.length == 0) {
                this.watermarkText = '<span>There are no rules to display for this group.</span>'
            }
        }).catch(err => this.watermarkText = '<span>There are no rules to display for this group.</span>');
    }

    /**
     * Called whenever the list of rules is updated or changed.
     * Gets all group rules and then creates a CSV data file for them.
     *
     * @func doUpdate
     */
    doUpdate(orgId, extensionId) {
        this._rules.getGroupContentV2(this.root).then(res => {
            this.ruleList = res
            this.createCsv();

            if(this.ruleList.length == 0) {
                this.watermarkText = '<span>There are no rules to display for this group.</span>'
            }
        }).catch(err => this.watermarkText = '<span>There are no rules to display for this group.</span>');
    }


    /**
     * Creates a CSV file that shows the list of rules added to a customer in a portable table.
     *
     * @func createCsv
     */
    createCsv() {
        this.data = [];
        this.ruleList.forEach(rule => {
            const ruleData = {
                ruleTriggerCode: rule.groupRuleId.triggerCode,
                physicianTitle: rule.physicianTitle,
                triggerSilent: rule.triggerSilent
            };
            this.data.push(ruleData);
        })
    }

    /**
     * Saves a copy of the csv to your local drive.
     *
     * @func exportCsv
     */
    exportCsv() {
        let nameForFile = '';
        if(this.org && this.org.description) {
            nameForFile = this.org.description.replaceAll(" ", "")
        }
        const options = {
            filename: nameForFile + 'Export-'+Date.now(),
            fieldSeparator: ',',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: true,
            title: 'Company Package Export CSV',
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: true
        };

        const csvExporter = new ExportToCsv(options);
        if(!(this.data === undefined) && this.data !== null && Array.isArray(this.data) && this.data.length > 0) {
            csvExporter.generateCsv(this.data);
        } else {
            this.alertService.showAlert("Invalid Export. No data provided.");
        }
    }

    openDeleteModal() {
        this.isDisabled = true;
        $(this._el.nativeElement).find('#modal-delete-rule').show();
    }

    closeDeleteModal() {
        if (this.cboxConfirmRef) {
            this.cboxConfirmRef.nativeElement.checked = false;
        }
        $(this._el.nativeElement).find('#modal-delete-rule').hide();
    }

    /**
     * Toggles checkbox that disables the delete button by default.
     *
     * @func toggleDisabled
     */
    toggleDisabled() {
        this.isDisabled = !this.isDisabled;
    }

    /**
     * Deletes the selected rules.
     *
     * @async
     * @func onDelete
     */
    onDelete() {
        try {
            const ruleEditsCopy = JSON.parse(JSON.stringify(this.ruleEdits));
            const assignedOrgId = this.getAssignedOrgId(ruleEditsCopy);
            const groupOid = this.org.rootId + '%2B' + this.org.extensionId;

            this._org.deleteRulesForGroupV2(ruleEditsCopy, groupOid, assignedOrgId)
                .then(res => {
                    this.ruleDeleted.emit(null);
                    this.doUpdate(this.org.rootId, this.org.extensionId);
                    this.ruleEdits = [];
                });
            this.closeDeleteModal();
        } catch (e) {
            this.alertService.showAlert("An error occurred while attempting to update group rules.");
            this.closeDeleteModal();
            const orgRoot = this.root.split('+')[0];
            const groupExt = this.root.split('+')[1];
            this.doUpdate(orgRoot, groupExt);
        }
    }

    private getAssignedOrgId(rules: Rule[]): string {
        const assignedOrgId = rules
            .map(rule => rule.orgId || rule.org_id || rule.orgGroupRuleId?.orgId)
            .find(value => !!value);

        if (!assignedOrgId) {
            throw new Error('Unable to determine assigned org ID for selected rules.');
        }

        return assignedOrgId;
    }

    async onSubmit(triggerSilent) {
        try {
            const ruleEditsCopy = JSON.parse(JSON.stringify(this.ruleEdits));
            const assignedOrgId = this.getAssignedOrgId(ruleEditsCopy);
            let groupOid;
            if(this.org == undefined) {
                await this._org.getOrgInfo(this.root).then((org: CDSNode) => {
                    groupOid = org.rootId + '%2B' + org.extensionId;
                    this.org = org
                    return this.org;
                })

            } else {
                groupOid = this.org.rootId + '%2B' + this.org.extensionId;
            }

            this._org.updateRulesForGroupV2(ruleEditsCopy, triggerSilent, groupOid, assignedOrgId)
                .then(res => {
                    this.doUpdate(this.org.rootId, this.org.extensionId);
                    this.ruleEdits = [];
                });
            this.closeDropdown();
        } catch (e) {
            this.alertService.showAlert("An error occurred while attempting to update group rules.");
            const orgRoot = this.root.split('+')[0];
            const groupExt = this.root.split('+')[1];
            this.doUpdate(orgRoot, groupExt);
        }
    }

    openDropdown() {
        // if rule drop down is already visible, close it instead
        if ($('#stateOptions').is(':visible')) {
            this.closeDropdown();
        } else {
            $('#stateOptions').show();
        }
    }

    closeDropdown() {
        $('#stateOptions').hide();
    }

    /**
     * Uses the checkbox on the header to select/deselect all rules automatically.
     *
     * @func onHeaderSelect
     */
    onHeaderSelect() {
        if (this.ruleEdits.length >= this.ruleList.length) {
            this.ruleEdits.splice(0, this.ruleEdits.length);
            $('input[type=checkbox]').prop('checked', false);
        } else {
            this.ruleList.forEach(rule => this.ruleEdits.push(rule));
        }
    }

    /**
     * Checks or unchecks a rule to edit.
     *
     * @func onSelect
     * @param {Rule} rule - the rule to check/uncheck.
     */
    onSelect(rule: Rule) {
        const index = this.ruleEdits.indexOf(rule);
        if (index < 0) {
            this.ruleEdits.push(rule);
        }
        else {
            this.ruleEdits.splice(index, 1);
        }
        // if the final rule is unselected, automatically close rule dropdown
        if(this.ruleEdits.length == 0) {
            this.closeDropdown();
        }
    }

    getTreeRoots() {
        if (sessionStorage.getItem('treeRoots') == null) {
            this._cds.getTreeRoots().then((roots: string)  => {
                sessionStorage.setItem('treeRoots', roots);
                return roots.split(',');
            });
        } else {
            return sessionStorage.getItem('treeRoots').split(',');
        }
    }

    calculateGroupTopRoot() {
        let treeRoots: string[] = this.getTreeRoots();
        let tempRoot = this.root.replace('~', '2.16.840.1.113883.3.21');
        tempRoot = tempRoot.substring(0, tempRoot.indexOf('+'));

        treeRoots.forEach(root => {
            if(this.root.includes(root)) {
                return tempRoot.replace(root + '.', '').split('.').at(0);
            }
        });

        return tempRoot.replace('2.16.840.1.113883.3.21.', '').split('.').at(0);
    }

    /**
     * Displays the info for the selected rule.
     *
     * @async
     * @func onInfo
     * @param {Rule} rule - the rule to display info for.
     */
    onInfo(rule: Rule) {
        this.infoRule = rule;
        $('#modal-group-rule-details').show();
    }

    onCloseInfo() {
        $('#modal-group-rule-details').hide();
    }

}
